import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import CascaDeModal from './CascaDeModal';
import { TONE_BADGE } from '../../utils/statusColors';
import { campo } from '../../design';

/**
 * Confirmação padrão do HUB (§8 do guia).
 *
 * Existiam **113 chamadas de `window.confirm()`** espalhadas pelos componentes
 * e UM modal estilizado fazendo a mesma coisa. O diálogo nativo é o maior
 * desvio visual que sobrou: caixa branca do sistema operacional no meio de um
 * app escuro, sem distinguir ação destrutiva de ação comum, sem conseguir
 * destacar a consequência, e travando a thread do browser enquanto está aberto.
 *
 * Aqui a consequência é cidadã de primeira classe: `danger` pinta a ação, e
 * `consequence` é a linha que explica o que acontece DEPOIS de confirmar — que
 * é a informação que o `window.confirm` empurrava para dentro de um `\n\n`.
 *
 * Desde a Fase 3 a moldura é a `CascaDeModal` (tamanho xs, clique fora cancela),
 * como todo diálogo de ação do HUB.
 *
 * Uso:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Descartar licitação?', danger: true }))) return;
 *
 * O par do `window.prompt` é o `usePedirTexto`: mesma caixa, com um campo, e
 * devolve o texto (ou `null` se cancelou). Com `obrigatorio`, o botão só libera
 * com texto, e o texto vai aparado.
 *   const pedirTexto = usePedirTexto();
 *   const motivo = await pedirTexto({ title: 'Cancelar grupo?', label: 'Motivo', obrigatorio: true });
 *   if (motivo === null) return;
 */

export interface ConfirmOptions {
  title: string;
  /** Corpo. Aceita nó pra poder destacar nome do registro, valor, prazo. */
  message?: React.ReactNode;
  /** O que acontece DEPOIS de confirmar. Vai destacado, não solto no corpo. */
  consequence?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Ação destrutiva: botão vermelho e selo "irreversível". */
  danger?: boolean;
  /**
   * O selo "irreversível" segue o `danger` por padrão. `false` mantém o botão vermelho sem o selo, para a ação perigosa
   * que se desfaz (15/10/2026: "Parar o robô" para a disputa, mas pode ser iniciada de novo; o selo seria mentira).
   */
  irreversivel?: boolean;
  /**
   * Aviso só para LER (o resultado de uma ação que já aconteceu): um botão só, o `confirmLabel`. Sem ele, "Cancelar" ao
   * lado de um resultado sugere que dá para desfazer o que já foi feito.
   */
  semCancelar?: boolean;
}

export interface PedirTextoOptions extends ConfirmOptions {
  /** Rótulo do campo. */
  label: string;
  placeholder?: string;
  /** Sem texto, o botão de confirmar fica desabilitado. */
  obrigatorio?: boolean;
  /** Texto inicial do campo. */
  valorInicial?: string;
  /** Regra do campo: devolve a mensagem do problema, ou null se está bom. Enquanto
   *  houver problema, o botão de confirmar fica desabilitado e a mensagem aparece. */
  validar?: (texto: string) => string | null;
  /** Campo de uma linha (input) em vez de textarea; Enter confirma. */
  linhaUnica?: boolean;
  /** Só para mostrar e copiar (link, código): campo somente leitura, já selecionado. */
  somenteLeitura?: boolean;
}

type Pedido =
  | { tipo: 'confirmar'; opts: ConfirmOptions; resolver: (ok: boolean) => void }
  | { tipo: 'texto'; opts: PedirTextoOptions; resolver: (texto: string | null) => void };

interface Api {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  pedirTexto: (opts: PedirTextoOptions) => Promise<string | null>;
}

const ConfirmContext = createContext<Api | null>(null);

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback honesto: sem o provider, cai no nativo em vez de quebrar a tela.
    return async (opts: ConfirmOptions) =>
      window.confirm([opts.title, typeof opts.message === 'string' ? opts.message : '']
        .filter(Boolean).join('\n\n'));
  }
  return ctx.confirm;
};

/**
 * Confirmação de ação que NÃO SE DESFAZ (excluir, revogar, cancelar assinatura, apagar
 * regra): botão vermelho e selo "irreversível". Recebe o texto no formato do antigo
 * `window.confirm` (primeiro parágrafo = título, o resto = corpo), para a migração de
 * 06/10/2026 trocar o diálogo sem reescrever os textos. ⛔ Ação destrutiva nova não usa
 * `window.confirm`: `scripts/check-confirm-nativo.js` trava isso no build.
 */
export const useConfirmarPerigo = () => {
  const confirm = useConfirm();
  return (texto: string) => {
    const [titulo, ...resto] = String(texto).split('\n\n');
    return confirm({ title: titulo, message: resto.join('\n\n') || undefined, danger: true });
  };
};

export const usePedirTexto = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    return async (opts: PedirTextoOptions) => {
      const r = window.prompt([opts.title, opts.label].filter(Boolean).join('\n\n'), opts.valorInicial || '');
      if (r === null) return null;
      const t = r.trim();
      return opts.obrigatorio && !t ? null : t;
    };
  }
  return ctx.pedirTexto;
};

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [texto, setTexto] = useState('');
  const pedidoRef = useRef<Pedido | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => new Promise<boolean>((resolver) => {
    const p: Pedido = { tipo: 'confirmar', opts, resolver };
    pedidoRef.current = p;
    setPedido(p);
  }), []);

  const pedirTexto = useCallback((opts: PedirTextoOptions) => new Promise<string | null>((resolver) => {
    const p: Pedido = { tipo: 'texto', opts, resolver };
    pedidoRef.current = p;
    setTexto(opts.valorInicial || '');
    setPedido(p);
  }), []);

  const fechar = useCallback((ok: boolean) => {
    const p = pedidoRef.current;
    pedidoRef.current = null;
    setPedido(null);
    if (!p) return;
    if (p.tipo === 'confirmar') p.resolver(ok);
    else p.resolver(ok ? texto.trim() : null);
  }, [texto]);

  const value = useMemo<Api>(() => ({ confirm, pedirTexto }), [confirm, pedirTexto]);

  const opts = pedido?.opts;
  const problema = pedido?.tipo === 'texto' && pedido.opts.validar && texto.trim() ? pedido.opts.validar(texto.trim()) : null;
  const bloqueado = pedido?.tipo === 'texto' && ((!!pedido.opts.obrigatorio && !texto.trim()) || !!problema);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {pedido && opts && (
        // Acima de qualquer modal da tela (z-100): a confirmação sempre nasce de
        // dentro de outra coisa. Clique fora e × valem Cancelar.
        <CascaDeModal
          largura="xs"
          zIndex="z-[100]"
          fecharAoClicarFora
          titulo={opts.title}
          quebrarTitulo
          selos={(opts.irreversivel ?? opts.danger) ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TONE_BADGE.danger}`}>irreversível</span> : undefined}
          onClose={() => fechar(false)}
          corpoClassName="px-5 py-4"
          rodape={
            <div className="ml-auto flex gap-2">
              {!opts.semCancelar && (
                <button
                  onClick={() => fechar(false)}
                  className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm transition-colors"
                >
                  {opts.cancelLabel || 'Cancelar'}
                </button>
              )}
              <button
                autoFocus={pedido.tipo === 'confirmar'}
                disabled={bloqueado}
                onClick={() => fechar(true)}
                className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50 ${
                  opts.danger ? 'bg-red-600 hover:bg-red-500' : 'bg-primary hover:bg-primary-strong'
                }`}
              >
                {opts.confirmLabel || 'Confirmar'}
              </button>
            </div>
          }
        >
          {opts.message && (
            <div className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">{opts.message}</div>
          )}
          {opts.consequence && (
            <div className="mt-3 rounded-lg border border-central-borda bg-central-fundo/50 px-3 py-2 text-xs text-slate-300">
              {opts.consequence}
            </div>
          )}
          {pedido.tipo === 'texto' && (
            <div className={opts.message || opts.consequence ? 'mt-3' : ''}>
              <label className="block text-xs font-medium text-slate-400 mb-1" htmlFor="pedir-texto">
                {pedido.opts.label}{pedido.opts.obrigatorio ? ' *' : ''}
              </label>
              {pedido.opts.linhaUnica || pedido.opts.somenteLeitura ? (
                <input
                  id="pedir-texto"
                  autoFocus
                  readOnly={pedido.opts.somenteLeitura}
                  onFocus={pedido.opts.somenteLeitura ? (e) => e.currentTarget.select() : undefined}
                  value={texto}
                  placeholder={pedido.opts.placeholder}
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !bloqueado) fechar(true); }}
                  className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none ${campo}`}
                />
              ) : (
                <textarea
                  id="pedir-texto"
                  autoFocus
                  rows={3}
                  value={texto}
                  placeholder={pedido.opts.placeholder}
                  onChange={(e) => setTexto(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none ${campo}`}
                />
              )}
              {problema && <p className="mt-1 text-xs text-amber-400">{problema}</p>}
            </div>
          )}
        </CascaDeModal>
      )}
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;
